/**
 * Bank instalment plans — which bank offers which term, by which method, and at what rate.
 *
 * Source: the Astern "Bank Installment Plan (Merchant/Payment link by credit card)" chart
 * supplied by the user 2026-10-28.
 *
 * ONE RATE FOR EVERY BANK AT 12 / 24 / 36 MONTHS. At 60 months it is "By bank" and the
 * spread is wide — 7.20% to 14.00%. On a RM100,000 cash job that is RM107,760 against
 * RM116,280, a difference of RM8,520 on the same system. A single 60-month price cannot
 * be right for more than one of them, which is why the RSC tile shows a button instead of
 * a figure. (user 2026-10-29: "60m cc shd not show the direct answer, cause each bank
 * diff")
 *
 * AVAILABILITY MATTERS AS MUCH AS RATE. Only six banks offer 60 months at all, CIMB has no
 * payment link on any term, and three banks drop to link-only at 36. A quote or a payment
 * link offered on a plan the bank does not run is a promise nobody can keep.
 */

/** Flat rates, identical across every bank that offers the term. */
export const CC_RATE = { 12: 4.5, 24: 6.5, 36: 7.5 };

export const TERMINAL = 'terminal';
export const LINK = 'link';

/**
 * methods: which of terminal / link the bank runs for that term.
 * rate:    60-month only — percent. A bank whose two methods differ carries both.
 * Absent term = the bank does not offer it.
 */
export const BANK_PLANS = {
    'Maybank': {
        12: { methods: [TERMINAL, LINK] }, 24: { methods: [TERMINAL, LINK] },
        36: { methods: [TERMINAL, LINK] }, 60: { methods: [TERMINAL], rate: 10.0 },
    },
    'Standard Chartered': {
        12: { methods: [TERMINAL, LINK] }, 24: { methods: [TERMINAL, LINK] },
        36: { methods: [TERMINAL, LINK] }, 60: { methods: [TERMINAL], rate: 10.0 },
    },
    'Public Bank': {
        12: { methods: [TERMINAL, LINK] }, 24: { methods: [TERMINAL, LINK] },
        36: { methods: [TERMINAL, LINK] }, 60: { methods: [TERMINAL, LINK], rate: 10.0 },
    },
    'AmBank Group': {
        12: { methods: [TERMINAL, LINK] }, 24: { methods: [TERMINAL, LINK] },
        36: { methods: [LINK] },
        visaOnlyLink: true,
    },
    'Hong Leong Bank': {
        12: { methods: [TERMINAL, LINK] }, 24: { methods: [TERMINAL, LINK] },
        36: { methods: [TERMINAL, LINK] },
        // The only bank whose 60-month rate depends on HOW it is taken.
        60: { methods: [TERMINAL, LINK], rateByMethod: { [TERMINAL]: 7.2, [LINK]: 10.0 } },
    },
    'CIMB': {
        12: { methods: [TERMINAL] }, 24: { methods: [TERMINAL] },
        36: { methods: [TERMINAL] }, 60: { methods: [TERMINAL], rate: 12.0 },
    },
    'HSBC': {
        12: { methods: [TERMINAL, LINK] }, 24: { methods: [TERMINAL, LINK] },
        36: { methods: [TERMINAL, LINK] }, 60: { methods: [TERMINAL], rate: 14.0 },
    },
    'RHB': {
        12: { methods: [TERMINAL, LINK] }, 24: { methods: [TERMINAL, LINK] },
        36: { methods: [LINK] },
    },
    'Affin Bank': {
        12: { methods: [LINK] }, 24: { methods: [LINK] }, 36: { methods: [LINK] },
    },
    'UOB': {
        12: { methods: [LINK] }, 24: { methods: [LINK] }, 36: { methods: [LINK] },
    },
    'OCBC': {
        12: { methods: [LINK] }, 24: { methods: [LINK] }, 36: { methods: [LINK] },
    },
    'Alliance Bank': {
        12: { methods: [TERMINAL, LINK] }, 24: { methods: [TERMINAL, LINK] },
        36: { methods: [LINK] },
        visaOnlyLink: true,
    },
};

export const BANKS = Object.keys(BANK_PLANS);

/** Round UP to the nearest RM10 — the same rule ccPricing uses, kept identical on purpose. */
const roundUp10 = (v) => Math.ceil((Number(v) || 0) / 10) * 10;

/** The rate a bank charges for a term, by method. null when it does not offer that term. */
export function rateFor(bank, term, method) {
    const plan = BANK_PLANS[bank] && BANK_PLANS[bank][term];
    if (!plan) return null;
    if (method && !plan.methods.includes(method)) return null;
    if (plan.rateByMethod) {
        // With no method asked for, quote the cheapest the bank can do.
        if (method) return plan.rateByMethod[method] ?? null;
        return Math.min(...Object.values(plan.rateByMethod));
    }
    return plan.rate != null ? plan.rate : (CC_RATE[term] ?? null);
}

/** cash → the price on that bank's plan. Same formula as everywhere else: cash ÷ (1 − rate). */
export function priceFor(cash, bank, term, method) {
    const c = Number(cash) || 0;
    const rate = rateFor(bank, term, method);
    if (c <= 0 || rate == null) return null;
    return roundUp10(c / (1 - rate / 100));
}

/** Banks offering `term`, optionally only by `method`. */
export const banksForTerm = (term, method) =>
    BANKS.filter((b) => rateFor(b, term, method) != null);

/**
 * Every priced option for a term, cheapest first — one row per method where a bank's
 * rate depends on it, so Hong Leong appears twice at 60 months and the 7.2% terminal
 * plan sorts to the top.
 */
export function planOptions(cash, term) {
    const out = [];
    BANKS.forEach((bank) => {
        const plan = BANK_PLANS[bank] && BANK_PLANS[bank][term];
        if (!plan) return;
        const split = !!plan.rateByMethod;
        (split ? plan.methods : [null]).forEach((method) => {
            const rate = rateFor(bank, term, method);
            if (rate == null) return;
            out.push({
                bank,
                method: method || null,
                methods: plan.methods,
                rate,
                price: priceFor(cash, bank, term, method),
                visaOnlyLink: !!BANK_PLANS[bank].visaOnlyLink,
            });
        });
    });
    return out.sort((a, b) => a.rate - b.rate || a.bank.localeCompare(b.bank));
}

/**
 * The cheapest and dearest priced option for a term.
 *
 * The RSC tile quotes the DEAREST — a figure every bank can beat is a figure no customer
 * is ever asked to top up. Quoting the cheapest (or a flat average) under-quotes anyone
 * who walks in with an HSBC card by thousands of ringgit.
 */
export function priceRange(cash, term) {
    const opts = planOptions(cash, term);
    const lo = opts[0];
    const hi = opts[opts.length - 1];
    // No plans, or nothing priced yet (a card with no cash on it). Returning an object
    // whose `max` is null just moves the crash into whoever formats it — a card showing
    // RM 0 before a bill is entered took exactly that route.
    if (!opts.length || lo.price == null) return null;
    return { min: lo.price, minRate: lo.rate, max: hi.price, maxRate: hi.rate, count: opts.length };
}

/**
 * One row per bank, one cell per term — the whole chart as a table.
 *
 * A cell holds a LIST of offers, not one price, because Hong Leong's 60-month rate
 * differs by method and the two have to sit side by side. Every other cell has exactly
 * one offer, and a term the bank does not run has none at all.
 */
export function bankRows(cash, terms = [12, 24, 36, 60]) {
    return BANKS.map((bank) => ({
        bank,
        visaOnlyLink: !!BANK_PLANS[bank].visaOnlyLink,
        cells: terms.map((term) => {
            const plan = BANK_PLANS[bank] && BANK_PLANS[bank][term];
            if (!plan) return { term, offers: [] };
            const split = !!plan.rateByMethod;
            return {
                term,
                offers: (split ? plan.methods : [null]).map((method) => ({
                    method,
                    methods: plan.methods,
                    rate: rateFor(bank, term, method),
                    price: priceFor(cash, bank, term, method),
                })),
            };
        }),
    }));
}

/** "T", "L" or "T/L" — how a plan can be taken, short enough for a table cell. */
export const methodTag = (methods = []) =>
    methods.map((m) => (m === TERMINAL ? 'T' : 'L')).join('/');

/** Does the price depend on which bank is chosen? True only at 60 months. */
export const rateVariesByBank = (term) => {
    const rates = new Set();
    BANKS.forEach((b) => {
        const plan = BANK_PLANS[b] && BANK_PLANS[b][term];
        if (!plan) return;
        if (plan.rateByMethod) Object.values(plan.rateByMethod).forEach((r) => rates.add(r));
        else rates.add(plan.rate != null ? plan.rate : CC_RATE[term]);
    });
    return rates.size > 1;
};

export const methodLabel = (m) => (m === TERMINAL ? 'Terminal' : m === LINK ? 'Payment Link' : '');
